// Plán volání na týden a měsíc (mockup „Plán volání“, varianta A se záložkou Plán, schváleno 1. 10. 2026).
// Mamka se ptala, jak zjistí, koho má v pondělí volat. Týden: sedm dní pod sebou, Měsíc: kalendář
// s počtem hovorů a seznam vybraného dne. Ukazují se jen kontakty s naplánovaným hovorem,
// zmeškané (datum v minulosti) patří k dnešku. Zkušební tréninky mají vlastní záložku.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Phone, Clock, CalendarDays } from "lucide-react";
import { ListovaniMesice, EmptyState } from "../ui.jsx";
import { Pil, PruhSkupiny, RadekKontaktu, KartaObal, StavPilulka } from "./salesUi.jsx";
import { salesGet, dnesIso, addDays, dowOf, dm, casBezNuly, telefonHezky } from "./salesApi.js";

const DNY = ["", "Pondělí", "Úterý", "Středa", "Čtvrtek", "Pátek", "Sobota", "Neděle"];
const MESICE = ["leden", "únor", "březen", "duben", "květen", "červen", "červenec", "srpen", "září", "říjen", "listopad", "prosinec"];
const PLANOVANE = new Set(["novy", "pokus", "odlozeno", "doplnit"]);

function nazevDne(iso, dnes) {
  if (iso === dnes) return `Dnes ${dm(iso)}`;
  if (iso === addDays(dnes, 1)) return `Zítra ${dm(iso)}`;
  return `${DNY[dowOf(iso)]} ${dm(iso)}`;
}
const posledniDen = (ym) => { const [y, m] = ym.split("-").map(Number); return new Date(Date.UTC(y, m, 0)).getUTCDate(); };
const posunMesic = (ym, n) => { const [y, m] = ym.split("-").map(Number); const d = new Date(Date.UTC(y, m - 1 + n, 1)); return d.toISOString().substring(0, 7); };

function Den({ iso, dnes, kontakty, vybrany, onOtevrit }) {
  if (!kontakty.length) {
    return <div className="flex min-h-[48px] items-center rounded-2xl border border-slate-200 bg-white/60 px-4 text-[13px] font-semibold text-slate-400">{nazevDne(iso, dnes)}<span className="ml-auto">nikdo</span></div>;
  }
  return (
    <KartaObal>
      <PruhSkupiny title={nazevDne(iso, dnes)} pocet={kontakty.length} ton={iso === dnes ? "pozor" : "znacka"} />
      {kontakty.map((k, i) => (
        <RadekKontaktu key={k.id} prvni={i === 0} jmeno={k.jmeno} vybrany={vybrany === k.id} onOtevrit={() => onOtevrit(k.id)} poznamka={k.poznamka}
          pilulky={<>
            <StavPilulka k={k} />
            {k.dalsi < dnes && <Pil ton="pozor" casti={["zmeškané", `od ${dm(k.dalsi)}`]} />}
            {k.dalsi_cas && <Pil ton="info" ikona={Clock} casti={casBezNuly(k.dalsi_cas)} />}
            {k.telefon && <Pil ton="neutralni" ikona={Phone} casti={telefonHezky(k.telefon)} />}
          </>} />
      ))}
    </KartaObal>
  );
}

export default function PlanVolani({ rezim = "tyden", vybrany = null, onOtevrit }) {
  const dnes = dnesIso();
  const [data, setData] = useState(null);
  const [chyba, setChyba] = useState("");
  const [tyden, setTyden] = useState(0);
  const [mesic, setMesic] = useState(dnes.substring(0, 7));
  const [den, setDen] = useState(null);

  const nacti = useCallback(async () => {
    try { setData(await salesGet("kontakty", { pohled: "rozpracovane" })); setChyba(""); } catch (e) { setChyba(e.message); }
  }, []);
  useEffect(() => { nacti(); }, [nacti]);
  useEffect(() => { const h = () => nacti(); window.addEventListener("fw40-sales-obnovit", h); return () => window.removeEventListener("fw40-sales-obnovit", h); }, [nacti]);

  // den → kontakty; zmeškané patří k dnešku
  const poDnech = useMemo(() => {
    const m = new Map();
    for (const k of data?.seznam || []) {
      if (!PLANOVANE.has(k.stav) || !k.dalsi) continue;
      const d = k.dalsi < dnes ? dnes : k.dalsi;
      if (!m.has(d)) m.set(d, []);
      m.get(d).push(k);
    }
    for (const arr of m.values()) arr.sort((a, b) => String(a.dalsi_cas || "99").localeCompare(String(b.dalsi_cas || "99")) || a.jmeno.localeCompare(b.jmeno, "cs"));
    return m;
  }, [data, dnes]);
  const naDen = (iso) => poDnech.get(iso) || [];

  if (chyba) return <KartaObal><EmptyState icon={CalendarDays} title="Plán se nenačetl" hint={chyba} /></KartaObal>;
  if (!data) return <div className="space-y-2">{[0, 1, 2, 3].map((i) => <div key={i} className="h-14 animate-pulse rounded-xl bg-brand-light" />)}</div>;

  if (rezim === "tyden") {
    const od = addDays(dnes, tyden * 7);
    const dny = Array.from({ length: 7 }, (_, i) => addDays(od, i));
    return (
      <div className="space-y-3">
        <ListovaniMesice popis={`${dm(dny[0])} až ${dm(dny[6])}`} onZpet={() => setTyden((t) => t - 1)} zpetVypnuto={tyden <= 0} onVpred={() => setTyden((t) => t + 1)} />
        {dny.map((iso) => <Den key={iso} iso={iso} dnes={dnes} kontakty={naDen(iso)} vybrany={vybrany} onOtevrit={onOtevrit} />)}
      </div>
    );
  }

  // Měsíc
  const [y, m] = mesic.split("-").map(Number);
  const prvni = `${mesic}-01`;
  const posun = dowOf(prvni) - 1;
  const pocetDni = posledniDen(mesic);
  const dnyMesice = Array.from({ length: pocetDni }, (_, i) => `${mesic}-${String(i + 1).padStart(2, "0")}`);
  const vychozi = dnyMesice.find((d) => d >= dnes && naDen(d).length) || (dnes.startsWith(mesic) ? dnes : prvni);
  const aktivniDen = den && den.startsWith(mesic) ? den : vychozi;
  return (
    <div className="space-y-3">
      <ListovaniMesice popis={`${MESICE[m - 1]} ${y}`} onZpet={() => setMesic((x) => posunMesic(x, -1))} zpetVypnuto={mesic <= dnes.substring(0, 7)} onVpred={() => setMesic((x) => posunMesic(x, 1))} />
      <KartaObal>
        <div className="p-2 sm:p-3">
          <div className="mb-1 grid grid-cols-7 gap-1 text-center text-[12px] font-bold uppercase tracking-wider text-slate-400">
            {["po", "út", "st", "čt", "pá", "so", "ne"].map((x) => <span key={x}>{x}</span>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: posun }, (_, i) => <span key={`p${i}`} />)}
            {dnyMesice.map((iso) => {
              const n = naDen(iso).length;
              const zapnuto = iso === aktivniDen;
              const jeDnes = iso === dnes;
              return (
                <button key={iso} type="button" onClick={() => setDen(iso)} aria-pressed={zapnuto} aria-label={`${nazevDne(iso, dnes)}, ${n ? `hovorů ${n}` : "nikdo"}`}
                  className={`flex min-h-[52px] flex-col justify-between rounded-xl border p-1.5 text-left transition sm:min-h-[64px] ${zapnuto ? "border-brand bg-brand-light ring-2 ring-brand/25" : "border-slate-200 bg-white"} ${iso < dnes ? "opacity-50" : ""}`}>
                  <span className={`text-[13px] tabular-nums ${jeDnes ? "font-extrabold text-brand-deep" : "font-semibold text-slate-600"}`}>{Number(iso.substring(8))}</span>
                  {n > 0 && <span className="self-end rounded-full bg-brand px-1.5 text-[12px] font-bold leading-5 text-white tabular-nums">{n}</span>}
                </button>
              );
            })}
          </div>
        </div>
      </KartaObal>
      <Den iso={aktivniDen} dnes={dnes} kontakty={naDen(aktivniDen)} vybrany={vybrany} onOtevrit={onOtevrit} />
    </div>
  );
}
